import { createFileRoute } from '@tanstack/react-router';
import { EvaluationTypeIcon } from '@/app/components/features/evaluations/EvaluationTypeIcon';
import { getTenderPackageDetailsQuery } from '@/app/hooks/queries/useTenderPackages';
import {
  Breadcrumb,
  BreadcrumbMenu,
  BreadcrumbMenuItem,
  BreadcrumbItem,
  BreadcrumbLink,
  BreadcrumbList,
  BreadcrumbPage,
  BreadcrumbSeparator,
} from '@/app/ui/components/breadcrumb';
import {
  Header,
  HeaderSidechatButton,
  HeaderBackButton,
  HeaderSidebarTrigger,
} from '@/app/ui/components/header';
import { RunAnalysisHeaderButton } from '@/app/components/features/evaluations/RunAnalysisButton';
import { DocumentTypes, EvaluationTypeLabelsEnum, AmbiguityTypes } from '@/app/utils/constants';
import { AuctionIcon, Refresh01Icon } from '@hugeicons/core-free-icons';
import { HugeiconsIcon } from '@hugeicons/react';
import { useTranslations } from 'use-intl';
import { getBidDetailsQuery } from '@/app/hooks/queries/useBids';
import { AmbiguitiesPageContent } from '@/app/components/features/evaluations/Ambiguities/AmbiguitiesPageContent';
import { ProjectTypeIcon } from '@/app/components/features/projects/ProjectTypeIcon';
import { getProjectDetailsQuery } from '@/app/hooks/queries/useProjects';
import { rerunAnalysisOpenAtom } from '@/app/components/features/evaluations/Ambiguities/store';
import { useSetAtom } from 'jotai';
import { useSuspenseQuery } from '@tanstack/react-query';

export const Route = createFileRoute(
  '/_authed/project/$projectId/_layout/(evaluation)/tender-packages/$tenderPackageId/bids/$bidId/_layout/ambiguities/',
)({
  component: RouteComponent,
});

function RouteComponent() {
  const tGeneral = useTranslations('general');
  const tFeature = useTranslations('features.ambiguities');
  const tBid = useTranslations('pages.bids');
  const { bidId, tenderPackageId, projectId } = Route.useParams();
  const { data: project } = useSuspenseQuery(getProjectDetailsQuery(projectId));
  const { data: evaluation } = useSuspenseQuery(getTenderPackageDetailsQuery(tenderPackageId));
  const { data: bid } = useSuspenseQuery(getBidDetailsQuery(bidId));
  const setRerunAnalysisOpen = useSetAtom(rerunAnalysisOpenAtom);

  return (
    <>
      <Header>
        <HeaderBackButton
          to="/project/$projectId/tender-packages/$tenderPackageId/bids/$bidId"
          params={{ projectId, tenderPackageId, bidId }}
        />
        <HeaderSidebarTrigger />
        <Breadcrumb>
          <BreadcrumbList>
            <BreadcrumbMenu>
              <BreadcrumbMenuItem
                to="/project/$projectId/tender-packages/$tenderPackageId"
                params={{ projectId, tenderPackageId }}
              >
                <EvaluationTypeIcon type={EvaluationTypeLabelsEnum.TENDER_PACKAGE} />
                {evaluation.name}
              </BreadcrumbMenuItem>
              <BreadcrumbMenuItem to="/project/$projectId" params={{ projectId }}>
                <ProjectTypeIcon type={project.type} />
                {project.name}
              </BreadcrumbMenuItem>
            </BreadcrumbMenu>
            <BreadcrumbSeparator className="max-md:hidden" />
            <BreadcrumbItem>
              <BreadcrumbLink
                to="/project/$projectId/tender-packages/$tenderPackageId/bids"
                params={{ projectId, tenderPackageId }}
              >
                <HugeiconsIcon icon={AuctionIcon} />
                {tBid('title')}
              </BreadcrumbLink>
            </BreadcrumbItem>
            <BreadcrumbSeparator />
            <BreadcrumbItem>
              <BreadcrumbLink
                to="/project/$projectId/tender-packages/$tenderPackageId/bids/$bidId"
                params={{ projectId, tenderPackageId, bidId }}
              >
                <span className="line-clamp-1">{bid.bidAdditionalName || bid.companyName}</span>
              </BreadcrumbLink>
            </BreadcrumbItem>
            <BreadcrumbSeparator />
            <BreadcrumbItem>
              <BreadcrumbPage>{tFeature('title')}</BreadcrumbPage>
            </BreadcrumbItem>
          </BreadcrumbList>
        </Breadcrumb>
        <HeaderSidechatButton />
        <RunAnalysisHeaderButton
          entityId={bidId}
          documentType={DocumentTypes.BID}
          onClick={() => setRerunAnalysisOpen(true)}
          aria-label={tGeneral('reRunAnalysis')}
        >
          <HugeiconsIcon icon={Refresh01Icon} />
        </RunAnalysisHeaderButton>
      </Header>
      <AmbiguitiesPageContent ambiguityType={AmbiguityTypes.BID} id={bidId} />
    </>
  );
}
